import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { ToastService } from '@capturum/ui/api';
import { CapturumButtonModule } from '@capturum/ui/button';
import { AuthenticationParameterBody } from '@features/applications/interfaces/application.interface';
import { FieldArrayType, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApplicationApiService } from '@shared/services/application-api.service';
import { ConfirmationService } from 'primeng/api';

@Component({
  selector: 'app-formly-application-auth-params-type',
  imports: [FormlyModule, CapturumButtonModule, TranslateModule, CommonModule],
  templateUrl: './formly-application-auth-params-type.component.html',
  styleUrl: './formly-application-auth-params-type.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyApplicationAuthParamsTypeComponent extends FieldArrayType implements OnInit, OnDestroy {
  public isAddingParam = signal(false);
  public applicationId: string;

  private apiService = inject(ApplicationApiService);
  private translateService = inject(TranslateService);
  private toastService = inject(ToastService);
  private confirmationService = inject(ConfirmationService);

  public ngOnInit(): void {
    this.applicationId = this.props.applicationId;

    if (!this.field.model?.length) {
      this.add();
    }
  }

  public ngOnDestroy(): void {
    const emptyParams = this.model?.filter((param) => {
      return !param.id;
    });

    if (emptyParams?.length) {
      emptyParams.forEach((param) => {
        this.remove(this.model.indexOf(param));
      });
    }
  }

  public override add(): void {
    this.isAddingParam.set(true);
    super.add();
  }

  public override remove(index: number): void {
    if (this.model[index]?.id) {
      const paremeterId = this.model[index].id;

      this.confirmationService.confirm({
        header: this.translateService.instant('confirm.title'),
        message: this.translateService.instant('confirm.entity.delete.confirmation', {
          entity: this.translateService.instant('application.authentication-parameter.name')?.toLowerCase(),
        }),
        accept: () => {
          this.apiService.deleteAuthenticationParameters(this.applicationId, paremeterId).subscribe({
            next: () => {
              super.remove(index);
              this.isAddingParam.set(false);
              this.toastService.success(
                this.translateService.instant('general.success'),
                this.translateService.instant('list.item_deleted'),
              );
            },
          });
        },
      });
    } else {
      super.remove(index);
      this.isAddingParam.set(false);
    }
  }

  public save(index: number): void {
    const payload = this.model[index] as AuthenticationParameterBody;

    const saveMethod = payload?.id ? 'updateAuthenticationParameters' : 'createAuthenticationParameters';

    this.apiService[saveMethod](this.applicationId, payload, payload?.id).subscribe({
      next: (response) => {
        this.model[index] = response;
        this.isAddingParam.set(false);
        this.toastService.success(
          this.translateService.instant('general.success'),
          this.translateService.instant('list.item_created'),
        );
      },
    });
  }
}
